import store from '@/store/store';
import { getUsageInsightsToken } from '@/utils/auth0Token';
import { jwtDecode } from 'jwt-decode';
import { currentUserApi } from '@/redux/services/portal/user.api';
import { API_BIOVIZ, AUTH_DOMAIN, GLOBALVIZ_USAGE_INGEST_URL } from '@/config/env.config';

/**
 * Portal funnel emit for Usage Insights MVP workstream B.
 *
 * Posts `portal_results_view` and `portal_results_download` to GlobalViz ingest.
 * Auth is `Authorization: Bearer` via the Auth0 SDK, using the PortalApi
 * audience already accepted by GlobalViz. No cookies are attached
 * (`credentials: 'omit'`).
 *
 * The token is a PortalApi access token. It only goes to the `API_BIOVIZ`
 * origin over https (http on loopback only), never across a redirect, and
 * with no Referer. Every id comes from the platform CSPRNG. When none is
 * available nothing is sent.
 *
 * Option B (locked): login and logout are not emitted. First IBP engagement is
 * inferred from the first GlobalViz session / module_enter. This module does
 * not own tab dwell, the event store, or any PortalApi write endpoint.
 * PublishDate stays on the project record; the warehouse joins it.
 */

export const PORTAL_RESULTS_VIEW = 'portal_results_view';
export const PORTAL_RESULTS_DOWNLOAD = 'portal_results_download';
export const USAGE_INSIGHTS_APP = 'aperture';
export const USAGE_INSIGHTS_SCHEMA_VERSION = 1;
export const USAGE_EMIT_TIMEOUT_MS = 3000;
export const USAGE_SESSION_STORAGE_KEY = 'usage_insights_session_id';
export const USAGE_BROWSER_TAB_STORAGE_KEY = 'usage_insights_browser_tab_id';
export const USAGE_SESSION_OWNER_STORAGE_KEY = 'usage_insights_session_owner';

// session_id is shared across tabs for one login (localStorage, cleared on logout).
// browser_tab_id is per tab (sessionStorage) so hidden tabs stay distinct.

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Match GlobalViz's opaque /users/current identifier contract, not Auth0 sub.
const PORTAL_USER_ID_PATTERN = /^[A-Za-z0-9_.:-]{1,64}$/;
const LOOPBACK_HOSTS = ['localhost', '127.0.0.1', '[::1]'];
const INGEST_PATH = '/usage-insights/ingest';
const REFUSAL_COOLDOWN_MS = 5 * 60 * 1000;
const FAILURE_COOLDOWN_MS = 30 * 1000;
const DEFAULT_RATE_COOLDOWN_MS = 60 * 1000;
// Allow a view and an immediate download together without queuing more work.
const MAX_IN_FLIGHT = 2;

let currentSessionUserId = null;
const activeControllers = new Set();
let blockedUntil = 0;
let disabled = false;
let refusalStatus = null;
let memoryOnlyOwner = false;

const memoryIds = {
	sessionId: null,
	browserTabId: null,
};
const memoryOnlyIds = { sessionId: false, browserTabId: false };

const ALLOWED_EVENT_KEYS = [
	'event_name',
	'event_id',
	'user_id',
	'account_id',
	'project_id',
	'session_id',
	'browser_tab_id',
	'module_id',
	'app',
	'client_ts',
	'schema_version',
];

export function resetUsageInsightsForTests() {
	abortActiveRequests();
	activeControllers.clear();
	currentSessionUserId = null;
	memoryOnlyOwner = false;
	memoryIds.sessionId = null;
	memoryIds.browserTabId = null;
	memoryOnlyIds.sessionId = false;
	memoryOnlyIds.browserTabId = false;
	clearRefusalState();
}

function abortActiveRequests() {
	activeControllers.forEach((controller) => controller.abort());
}

function clearRefusalState() {
	blockedUntil = 0;
	disabled = false;
	refusalStatus = null;
}

// Logout in another tab clears localStorage; an old page must not keep the
// previous login's session or finish an emit with credentials acquired later.
if (typeof window !== 'undefined') {
	window.addEventListener('storage', (event) => {
		if (event.key === null || event.key === USAGE_SESSION_STORAGE_KEY
			|| event.key === USAGE_SESSION_OWNER_STORAGE_KEY) {
			memoryIds.sessionId = null;
			memoryOnlyIds.sessionId = false;
			memoryOnlyOwner = false;
			currentSessionUserId = null;
			clearRefusalState();
			abortActiveRequests();
		} else if (event.key?.startsWith('@@auth0spajs@@')) {
			// A changed SDK credential permits a fresh attempt after auth refusal.
			if ([401, 403].includes(refusalStatus)) clearRefusalState();
			abortActiveRequests();
		}
	});
}

function assertKnownEvent(eventName) {
	switch (eventName) {
		case PORTAL_RESULTS_VIEW:
		case PORTAL_RESULTS_DOWNLOAD:
			return eventName;
		default: {
			const unexpected = eventName;
			throw new Error(`Unsupported usage insights event: ${String(unexpected)}`);
		}
	}
}

function parseUrl(value) {
	if (typeof value !== 'string' || !value.trim()) return null;
	try {
		return new URL(value.trim());
	} catch (e) {
		return null;
	}
}

/**
 * Returns the URL to POST to, or '' when it is unset or not allowed. Only the
 * GlobalViz origin (`API_BIOVIZ`) qualifies because the request carries the
 * PortalApi bearer token.
 */
export function allowedIngestUrl(value, globalVizBase = API_BIOVIZ) {
	const url = parseUrl(value);
	const globalViz = parseUrl(globalVizBase);
	if (!url || !globalViz || url.origin !== globalViz.origin) return '';
	if (url.username || url.password) return '';
	if (url.pathname !== INGEST_PATH || url.search || url.hash) return '';
	const isLoopback = LOOPBACK_HOSTS.includes(url.hostname);
	if (url.protocol !== 'https:' && !(url.protocol === 'http:' && isLoopback)) return '';
	return url.href;
}

export function isPublishedResults(project) {
	if (!project || typeof project !== 'object') return false;
	const raw = project.publishDate ?? project.PublishDate;
	if (raw == null) return false;
	const value = String(raw).trim();
	return Boolean(value) && value !== 'null' && value !== 'undefined';
}

/** GUID-shaped values only: ProjectId, client ids, and the ids minted here. */
export function uuidOrNull(value) {
	if (typeof value !== 'string') return null;
	const text = value.trim();
	return UUID_PATTERN.test(text) ? text : null;
}

/**
 * `userId` from `/users/current`, the id Logger and Pendo already use and the
 * one GlobalViz resolves from the same token. Anything else means no event.
 */
export function opaqueUserIdFromPortalUser(user) {
	if (!user || typeof user !== 'object') return null;
	const raw = user.userId;
	if (typeof raw !== 'string') return null;
	const text = raw.trim();
	return PORTAL_USER_ID_PATTERN.test(text) ? text : null;
}

export function accountIdFromContext(project, user) {
	const clients = Array.isArray(project?.clients) ? project.clients : [];
	const candidates = [
		project?.accountId,
		project?.clientId,
		...clients.map((client) => client?.clientId),
		user?.accountId,
		user?.clientId,
	];
	for (const candidate of candidates) {
		const id = uuidOrNull(candidate);
		if (id) return id;
	}
	return null;
}

export function projectIdFromContext(project) {
	return uuidOrNull(project?.projectId);
}

export function readPortalUserFromState(state) {
	if (!state || typeof state !== 'object') return null;
	if (state.portal) {
		try {
			const endpoint = currentUserApi.endpoints.currentUser;
			const cached = [endpoint.select('')(state), endpoint.select(undefined)(state)];
			for (const entry of cached) {
				if (entry?.data && typeof entry.data === 'object') return entry.data;
			}
		} catch (e) {
			// Malformed RTK state falls through to the legacy slice.
		}
	}
	const legacy = state.currentUser?.data;
	if (legacy && typeof legacy === 'object') return legacy;
	return null;
}

/** Version 4 UUID from the platform CSPRNG, or null when there is none. */
export function createUsageId(cryptoRef = typeof crypto !== 'undefined' ? crypto : null) {
	if (!cryptoRef) return null;
	if (typeof cryptoRef.randomUUID === 'function') return cryptoRef.randomUUID();
	if (typeof cryptoRef.getRandomValues !== 'function') return null;
	const bytes = cryptoRef.getRandomValues(new Uint8Array(16));
	bytes[6] = (bytes[6] & 0x0f) | 0x40;
	bytes[8] = (bytes[8] & 0x3f) | 0x80;
	const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function readStorage(storage, key) {
	if (!storage || typeof storage.getItem !== 'function') return undefined;
	try {
		return storage.getItem(key) ?? null;
	} catch (e) {
		return undefined;
	}
}

function writeStorage(storage, key, value) {
	if (!storage || typeof storage.setItem !== 'function') return false;
	try {
		storage.setItem(key, value);
		return true;
	} catch (e) {
		// Private mode can reject writes. The in-memory id still covers this page.
		return false;
	}
}

function getOrCreateStoredId(storage, memoryKey, storageKey, newId) {
	const stored = readStorage(storage, storageKey);
	// A readable, empty store means logout/clear, not a reason to reuse memory.
	const existing = uuidOrNull(stored)
		|| (stored === undefined || memoryOnlyIds[memoryKey] ? uuidOrNull(memoryIds[memoryKey]) : null);
	if (existing) {
		memoryIds[memoryKey] = existing;
		return existing;
	}
	const created = uuidOrNull(newId());
	if (!created) return null;
	memoryIds[memoryKey] = created;
	memoryOnlyIds[memoryKey] = !writeStorage(storage, storageKey, created);
	return created;
}

function syncSessionUser(userId, storage) {
	const owner = memoryOnlyOwner ? currentSessionUserId
		: readStorage(storage, USAGE_SESSION_OWNER_STORAGE_KEY) ?? currentSessionUserId;
	if (owner !== userId || (currentSessionUserId && currentSessionUserId !== userId)
		|| (!memoryOnlyIds.sessionId && readStorage(storage, USAGE_SESSION_STORAGE_KEY) === null)) {
		memoryIds.sessionId = null;
		memoryOnlyIds.sessionId = false;
		clearRefusalState();
		abortActiveRequests();
		if (owner !== userId) {
			try { storage?.removeItem(USAGE_SESSION_STORAGE_KEY); } catch (e) { /* Storage can be unavailable. */ }
		}
	}
	currentSessionUserId = userId;
}

// These are defensive formatting/expiry checks, not signature verification.
// GlobalViz authenticates the JWT and derives the user/project authority.
export function isUsableUsageToken(token) {
	if (typeof token !== 'string' || token.length > 16384
		|| !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token)) return false;
	try {
		const header = jwtDecode(token, { header: true });
		const claims = jwtDecode(token);
		const audiences = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
		return header.alg === 'RS256' && claims.iss === `https://${AUTH_DOMAIN}/`
			&& audiences.includes('https://portal-api') && typeof claims.sub === 'string' && Boolean(claims.sub)
			&& typeof claims.exp === 'number' && Number.isFinite(claims.exp)
			&& claims.exp > Date.now() / 1000 + 5;
	} catch (e) {
		return false;
	}
}

function noteRefusal(response) {
	const now = Date.now();
	refusalStatus = response?.status ?? null;
	if ([404, 413, 415].includes(response?.status)) {
		disabled = true;
	} else if ([401, 403].includes(response?.status)) {
		blockedUntil = now + REFUSAL_COOLDOWN_MS;
	} else if (response?.status === 429) {
		const retryAfter = response.headers?.get('Retry-After');
		const delay = typeof retryAfter === 'string' && /^\d+$/.test(retryAfter)
			? Number(retryAfter) * 1000 : Date.parse(retryAfter) - now;
		blockedUntil = now + (Number.isFinite(delay)
			? Math.min(60 * 60 * 1000, Math.max(1000, delay)) : DEFAULT_RATE_COOLDOWN_MS);
	} else {
		blockedUntil = now + FAILURE_COOLDOWN_MS;
	}
}

export function buildUsageEvent({
	eventName,
	user,
	project,
	sessionId,
	browserTabId,
	clientTs,
	eventId,
}) {
	assertKnownEvent(eventName);
	const userId = opaqueUserIdFromPortalUser(user);
	const ids = [eventId, sessionId, browserTabId].map(uuidOrNull);
	if (!userId || ids.includes(null)) return null;
	const event = {
		event_name: eventName,
		event_id: ids[0],
		user_id: userId,
		account_id: accountIdFromContext(project, user),
		project_id: projectIdFromContext(project),
		session_id: ids[1],
		browser_tab_id: ids[2],
		module_id: null,
		app: USAGE_INSIGHTS_APP,
		client_ts: clientTs,
		schema_version: USAGE_INSIGHTS_SCHEMA_VERSION,
	};
	const extraKeys = Object.keys(event).filter((key) => !ALLOWED_EVENT_KEYS.includes(key));
	if (extraKeys.length > 0) {
		throw new Error(`Usage event has unexpected fields: ${extraKeys.join(', ')}`);
	}
	return event;
}

export async function emitPortalResultsEvent(eventName, project, deps = {}) {
	let controller;
	let timeout;
	let timedOut = false;
	try {
		assertKnownEvent(eventName);
		if (!isPublishedResults(project)) {
			return { sent: false, reason: 'unpublished' };
		}

		const ingestUrl = allowedIngestUrl(
			Object.prototype.hasOwnProperty.call(deps, 'ingestUrl') ? deps.ingestUrl : GLOBALVIZ_USAGE_INGEST_URL,
		);
		if (!ingestUrl) return { sent: false, reason: 'unconfigured' };

		const user = typeof deps.getPortalUser === 'function'
			? deps.getPortalUser()
			: readPortalUserFromState(store.getState());
		const userId = opaqueUserIdFromPortalUser(user);
		if (!userId) {
			return { sent: false, reason: 'missing_user' };
		}

		const newId = () => createUsageId(deps.crypto);
		const localStorageRef = deps.localStorage || (typeof window !== 'undefined' ? window.localStorage : null);
		const sessionStorageRef = deps.sessionStorage || (typeof window !== 'undefined' ? window.sessionStorage : null);
		syncSessionUser(userId, localStorageRef);
		if (disabled || Date.now() < blockedUntil) return { sent: false, reason: 'suppressed' };
		if (activeControllers.size >= MAX_IN_FLIGHT) return { sent: false, reason: 'in_flight' };
		const sessionId = getOrCreateStoredId(localStorageRef, 'sessionId', USAGE_SESSION_STORAGE_KEY, newId);
		const browserTabId = getOrCreateStoredId(sessionStorageRef, 'browserTabId', USAGE_BROWSER_TAB_STORAGE_KEY, newId);
		const eventId = deps.eventId ?? newId();
		if (!sessionId || !browserTabId || !eventId) {
			return { sent: false, reason: 'no_crypto' };
		}

		const event = buildUsageEvent({
			eventName,
			user,
			project,
			sessionId,
			browserTabId,
			clientTs: deps.clientTs || new Date().toISOString(),
			eventId,
		});
		if (!event) return { sent: false, reason: 'invalid_event' };
		memoryOnlyOwner = !writeStorage(localStorageRef, USAGE_SESSION_OWNER_STORAGE_KEY, userId);

		controller = new AbortController();
		activeControllers.add(controller);
		const cancelled = new Promise((resolve, reject) => {
			controller.signal.addEventListener('abort', () => reject(new Error('Usage emit cancelled')), { once: true });
		});
		timeout = setTimeout(() => {
			timedOut = true;
			controller.abort();
		}, USAGE_EMIT_TIMEOUT_MS);

		// Deferred: this is the broad PortalApi token GlobalViz already accepts. A
		// narrower analytics audience needs Auth0 and GlobalViz changes first.
		const token = await Promise.race([
			typeof deps.getToken === 'function' ? deps.getToken() : getUsageInsightsToken(),
			cancelled,
		]);
		if (!token) return { sent: false, reason: 'missing_token' };
		if (!isUsableUsageToken(token)) return { sent: false, reason: 'invalid_token' };
		const latestUser = typeof deps.getPortalUser === 'function'
			? deps.getPortalUser() : readPortalUserFromState(store.getState());
		const storedSession = readStorage(localStorageRef, USAGE_SESSION_STORAGE_KEY);
		if (controller.signal.aborted || opaqueUserIdFromPortalUser(latestUser) !== userId
			|| (!memoryOnlyIds.sessionId && storedSession !== undefined && storedSession !== sessionId)) {
			return { sent: false, reason: 'cancelled' };
		}

		const fetchImpl = deps.fetchImpl || fetch;
		const response = await Promise.race([fetchImpl(ingestUrl, {
			method: 'POST',
			credentials: 'omit',
			// A redirect would carry the bearer token to a URL that was never allowlisted.
			redirect: 'error',
			referrerPolicy: 'no-referrer',
			keepalive: true,
			signal: controller.signal,
			headers: {
				Authorization: `Bearer ${token}`,
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(event),
		}), cancelled]);
		if (!response || response.ok !== true) {
			noteRefusal(response);
			return { sent: false, reason: 'http', status: response ? response.status : null, event };
		}
		return { sent: true, event };
	} catch (e) {
		if (controller?.signal.aborted && !timedOut) return { sent: false, reason: 'cancelled' };
		blockedUntil = Date.now() + FAILURE_COOLDOWN_MS;
		if (timedOut) return { sent: false, reason: 'timeout' };
		return { sent: false, reason: 'error' };
	} finally {
		clearTimeout(timeout);
		if (controller) activeControllers.delete(controller);
	}
}

export function emitPortalResultsView(project, deps) {
	return emitPortalResultsEvent(PORTAL_RESULTS_VIEW, project, deps);
}

export function emitPortalResultsDownload(project, deps) {
	return emitPortalResultsEvent(PORTAL_RESULTS_DOWNLOAD, project, deps);
}
